import { useState } from 'react';
import { Bevestig, Kaart, Knop, Leeg, PaginaKop, SkeletRegels, WedstrijdRegel, useToast } from '../components/ui';
import { UitslagModaal } from '../components/Akkoord';
import { useAuth } from '../lib/auth';
import { chronologisch, alleSeizoenen, seizoenLabel, seizoenSleutel } from '../lib/elo';
import { fmtDatum, toonNaam } from '../lib/format';
import { useDeleteMatch, useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';
import { VASTSTELTERMIJN_DAGEN, ligtVast } from '../lib/validate';
import type { Match } from '../lib/types';

type Soort = 'alles' | '1v1' | '2v2' | 'toernooi' | 'wacht';

export default function Historie() {
  const { pouleId } = usePoule();
  const { players, matches, alleMatches, elo, zoekSpeler, laden } = useLeague(pouleId);
  const { isAdmin, profile } = useAuth();
  const ikId = profile?.id ?? '';
  const verwijderen = useDeleteMatch();
  const meld = useToast();

  const [speler, setSpeler] = useState('');
  const [seizoen, setSeizoen] = useState('');
  const [soort, setSoort] = useState<Soort>('alles');
  const [hoeveel, setHoeveel] = useState(25);
  const [teVerwijderen, setTeVerwijderen] = useState<Match | null>(null);
  const [aanTePassen, setAanTePassen] = useState<Match | null>(null);

  if (laden) {
    return (
      <>
        <PaginaKop titel="Historie" />
        <Kaart>
          <SkeletRegels aantal={10} />
        </Kaart>
      </>
    );
  }

  /* De historie toont ook wat nog niet bevestigd is; die regels dragen een
     merkteken en tellen (nog) niet mee voor de rating. */
  let potjes = chronologisch(alleMatches).reverse();
  if (speler) potjes = potjes.filter((m) => m.teamA.includes(speler) || m.teamB.includes(speler));
  if (seizoen) potjes = potjes.filter((m) => seizoenSleutel(m.playedAt) === seizoen);
  if (soort === '1v1' || soort === '2v2') potjes = potjes.filter((m) => m.mode === soort);
  if (soort === 'toernooi') potjes = potjes.filter((m) => Boolean(m.tournamentId));
  if (soort === 'wacht') potjes = potjes.filter((m) => m.status === 'wacht');

  const zichtbaar = potjes.slice(0, hoeveel);
  const gefilterd = Boolean(speler || seizoen || soort !== 'alles');

  /* Je eigen invoer terugtrekken kan zolang niemand hem bevestigd heeft; daarna
     telt hij mee voor ieders rating en is corrigeren met ✎ de weg. Dezelfde
     regel staat in verwijder_uitslag() in migratie 018. */
  function magZelfWeghalen(m: Match): boolean {
    return m.ingevoerdDoor === ikId && ikId !== '' && m.status === 'wacht' && !m.tournamentId;
  }

  async function verwijder(m: Match) {
    try {
      await verwijderen.mutateAsync(m.id);
      meld('Wedstrijd verwijderd; ratings zijn opnieuw doorgerekend.');
    } catch (err) {
      meld(err instanceof Error ? err.message : 'Verwijderen mislukte.', true);
    }
    setTeVerwijderen(null);
  }

  return (
    <>
      <PaginaKop
        titel="Historie"
        onder={`${potjes.length} ${potjes.length === 1 ? 'potje' : 'potjes'}${gefilterd ? ' na filteren' : ''}`}
      />

      <Kaart>
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="veld w-auto"
            value={speler}
            onChange={(e) => {
              setSpeler(e.target.value);
              setHoeveel(25);
            }}
          >
            <option value="">alle spelers</option>
            {players
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {toonNaam(p)}
                  {p.isActive ? '' : ' (gestopt)'}
                </option>
              ))}
          </select>

          <select
            className="veld w-auto"
            value={seizoen}
            onChange={(e) => {
              setSeizoen(e.target.value);
              setHoeveel(25);
            }}
          >
            <option value="">alle seizoenen</option>
            {alleSeizoenen(matches).map((k) => (
              <option key={k} value={k}>
                {seizoenLabel(k)}
              </option>
            ))}
          </select>

          <select
            className="veld w-auto"
            value={soort}
            onChange={(e) => {
              setSoort(e.target.value as Soort);
              setHoeveel(25);
            }}
          >
            <option value="alles">alle soorten</option>
            <option value="1v1">alleen 1 tegen 1</option>
            <option value="2v2">alleen 2 tegen 2</option>
            <option value="toernooi">alleen toernooipotjes</option>
            <option value="wacht">alleen wachtend op akkoord</option>
          </select>

          <span className="ml-auto text-xs text-krijt-500">
            Met het potlood zet je een stand recht; daarna moet de andere kant hem opnieuw
            bevestigen. Met het kruisje trek je een uitslag die je zelf invoerde terug, zolang de
            tegenpartij nog niet akkoord is.
            {isAdmin ? ' Als beheerder haal je elke uitslag weg.' : ''} Na{' '}
            {VASTSTELTERMIJN_DAGEN} dagen ligt een potje vast.
          </span>
        </div>
      </Kaart>

      <div className="mt-3.5">
        <Kaart>
          {zichtbaar.length ? (
            zichtbaar.map((m) => {
              /* Na een week is er niets meer te kiezen; dan verdwijnen beide
                 knoppen en blijft er een slotje staan. */
              const vast = ligtVast(m);
              return (
                <WedstrijdRegel
                  key={m.id}
                  potje={m}
                  zoekSpeler={zoekSpeler}
                  delta={elo.perPotje[m.id]?.deltaA}
                  deltaB={elo.perPotje[m.id]?.deltaB}
                  vast={vast}
                  opVerwijder={
                    !vast && (isAdmin || magZelfWeghalen(m)) ? () => setTeVerwijderen(m) : undefined
                  }
                  opAanpassen={
                    !vast && ([...m.teamA, ...m.teamB].includes(ikId) || isAdmin)
                      ? () => setAanTePassen(m)
                      : undefined
                  }
                />
              );
            })
          ) : (
            <Leeg tekst="Geen potjes die aan dit filter voldoen." ikoon="zoek" />
          )}
        </Kaart>
      </div>

      {potjes.length > zichtbaar.length ? (
        <div className="mt-3.5 text-center">
          <Knop onClick={() => setHoeveel((n) => n + 25)}>
            Nog 25 laden <span className="text-krijt-400">({potjes.length - zichtbaar.length} over)</span>
          </Knop>
        </div>
      ) : null}

      {aanTePassen ? (
        <UitslagModaal potje={aanTePassen} zoekSpeler={zoekSpeler} onSluit={() => setAanTePassen(null)} />
      ) : null}

      {teVerwijderen ? (
        <Bevestig
          titel="Wedstrijd verwijderen?"
          tekst={`${teVerwijderen.teamA.map((id) => toonNaam(zoekSpeler(id))).join(' & ')} – ${teVerwijderen.teamB
            .map((id) => toonNaam(zoekSpeler(id)))
            .join(' & ')} (${teVerwijderen.scoreA}–${teVerwijderen.scoreB}) van ${fmtDatum(
            teVerwijderen.playedAt
          )} wordt gewist. ${
            teVerwijderen.status === 'wacht'
              ? 'Hij wachtte nog op akkoord en telde dus nog niet mee voor de rating.'
              : 'Alle ratings vanaf dat moment worden opnieuw doorgerekend.'
          }`}
          knop="Verwijderen"
          onJa={() => void verwijder(teVerwijderen)}
          onNee={() => setTeVerwijderen(null)}
        />
      ) : null}
    </>
  );
}
